import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { CustomButton10Co, CustomButton10Ca, CustomButton10 } from '../../Templates';
import Spinner from '../../common/Spinner';
import { 
  FaClock, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaUsers, 
  FaMapMarkerAlt,
  FaBox
} from 'react-icons/fa';

const PickupWaitingStatus = ({ 
  delivery, 
  currentUser, 
  otherUser, 
  userRole, 
  packageData,
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  const [showNoShowDialog, setShowNoShowDialog] = useState(false);
  const [showArrivalDialog, setShowArrivalDialog] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);

  // Determine user roles for pickup
  const isPackageSender = currentUser?.id === delivery?.sender_id;
  const isCarrier = currentUser?.id === delivery?.carrier_id;

  // Check arrival status
  const senderHere = !!delivery?.pickup_sender_here_at;
  const carrierHere = !!delivery?.pickup_carrier_here_at;
  const bothPresent = senderHere && carrierHere;
  const timerStarted = !!delivery?.pickup_timer_started_at;

  // Calculate remaining time
  useEffect(() => {
    if (!delivery?.pickup_timer_started_at) return;

    const updateTimer = () => {
      const startTime = new Date(delivery.pickup_timer_started_at);
      const totalDuration = (delivery.meeting_duration_minutes || 3) * 60 * 1000; // Convert to milliseconds
      const additionalTime = (delivery.pickup_total_wait_time_minutes || 0) * 60 * 1000;
      const elapsed = Date.now() - startTime.getTime();
      const remaining = Math.max(0, totalDuration + additionalTime - elapsed);
      
      setTimeRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [delivery?.pickup_timer_started_at, delivery?.meeting_duration_minutes, delivery?.pickup_total_wait_time_minutes]);

  // Format time remaining
  const formatTimeRemaining = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get current location for location proof
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: new Date().toISOString()
            });
          },
          (error) => reject(error),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  };

  // Handle "I'm here" confirmation during timer
  const handleArrivalConfirmation = async (withLocation = false) => {
    setLoading(true);
    
    try {
      let locationProof = null;
      
      // Get location if requested
      if (withLocation) {
        try {
          locationProof = await getCurrentLocation();
        } catch (locError) {
          console.error('Location error:', locError);
          addToast({
            type: 'warning',
            title: t('package_actions.location_not_available'),
            description: locError.message || t('package_actions.location_not_available_desc')
          });
        }
      }

      const arrivalData = {
        timestamp: new Date().toISOString(),
        location_proof: locationProof,
        confirmed_by: currentUser.id
      };

      const updateField = isPackageSender ? 'pickup_sender_here_at' : 'pickup_carrier_here_at';
      
      const updateData = {
        [updateField]: arrivalData.timestamp,
        updated_at: new Date().toISOString()
      };

      // Add location proof if available
      if (locationProof) {
        const locationProofData = {
          [isPackageSender ? 'sender' : 'carrier']: arrivalData,
          timestamp: arrivalData.timestamp
        };
        updateData.pickup_location_proof = locationProofData;
      }

      // Check if both will be marked as here after this update
      const willBothBeHere = isPackageSender ? carrierHere : senderHere;
      
      // If both will be marked as here, change status to both_present
      if (willBothBeHere) {
        updateData.status = 'pickup_both_present';
      }

      // Update delivery
      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Call onUpdate with the updated delivery data
      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Get other user ID for notification
      const otherUserId = isPackageSender ? delivery.carrier_id : delivery.sender_id;

      if (willBothBeHere) {
        // Both are now marked as here
        await createNotification(
          otherUserId,
          'pickup_both_marked_here',
          t('package_actions.both_marked_here_notification'),
          delivery.id
        );

        await addSystemMessage(
          t('package_actions.both_marked_here_system') + 
          (locationProof ? ' ' + t('package_actions.location_shared') : '')
        );
      } else {
        // User marked as here during timer
        await createNotification(
          otherUserId,
          'pickup_marked_here',
          t('package_actions.marked_here_notification', { 
            role: userRole 
          }),
          delivery.id
        );

        await addSystemMessage(
          t('package_actions.marked_here_during_timer_system', { 
            role: userRole 
          }) + (locationProof ? ' ' + t('package_actions.location_shared') : '')
        );
      }

      addToast({
        type: 'success',
        title: t('package_actions.marked_as_here'),
        description: willBothBeHere ? 
          t('package_actions.both_present_ready_pickup') : 
          t('package_actions.other_notified_here')
      });

      setShowArrivalDialog(false);

    } catch (error) {
      console.error('Error confirming arrival:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_confirming_arrival')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle pickup completion (carrier only)
  const handlePickupComplete = async () => {
    setLoading(true);
    
    try {
      // Get location proof if possible
      let locationProof = null;
      try {
        locationProof = await getCurrentLocation();
      } catch (error) {
        console.log('Could not get location for pickup completion:', error);
      }

      const updateData = {
        status: 'package_picked_up',
        completion_time: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (locationProof) {
        updateData.pickup_completion_location_proof = locationProof;
      }

      // Update delivery status
      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Call onUpdate with the updated delivery data
      if (onUpdate && updatedDelivery) {
        onUpdate(updatedDelivery);
      }

      // Create notification for sender
      await createNotification(
        delivery.sender_id,
        'package_picked_up',
        t('package_actions.package_picked_up_notification'),
        delivery.id
      );

      // Add system message
      await addSystemMessage(
        t('package_actions.package_picked_up_system')
      );

      addToast({
        type: 'success',
        title: t('package_actions.pickup_completed'),
        description: t('package_actions.package_with_carrier')
      });

    } catch (error) {
      console.error('Error completing pickup:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_completing_pickup')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle extension request
  const handleExtensionRequest = async (additionalMinutes = 3) => {
    setLoading(true);
    
    try {
      const extensionData = {
        requested_by: currentUser.id,
        requested_at: new Date().toISOString(),
        additional_minutes: additionalMinutes,
        status: 'pending'
      };

      const currentExtensions = delivery.pickup_extension_requests || [];
      const updatedExtensions = [...currentExtensions, extensionData];

      const { error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          pickup_extension_requests: updatedExtensions,
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id);

      if (updateError) throw updateError;

      // Get other user ID for notification
      const otherUserId = isPackageSender ? delivery.carrier_id : delivery.sender_id;

      await createNotification(
        otherUserId,
        'pickup_extension_request',
        t('package_actions.extension_request_notification', { 
          role: userRole,
          minutes: additionalMinutes 
        }),
        delivery.id
      );

      await addSystemMessage(
        t('package_actions.extension_request_system', { 
          role: userRole,
          minutes: additionalMinutes 
        })
      );

      addToast({
        type: 'info',
        title: t('package_actions.extension_requested'),
        description: t('package_actions.waiting_for_approval')
      });

      setShowExtensionDialog(false);

    } catch (error) {
      console.error('Error requesting extension:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_requesting_extension')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle extension response
  const handleExtensionResponse = async (approve, extensionRequest) => {
    setLoading(true);
    
    try {
      const updatedExtensions = (delivery.pickup_extension_requests || []).map(req => 
        req.requested_at === extensionRequest.requested_at 
          ? { ...req, status: approve ? 'approved' : 'denied', responded_at: new Date().toISOString() }
          : req
      );

      const updateData = {
        pickup_extension_requests: updatedExtensions,
        updated_at: new Date().toISOString()
      };

      if (approve) {
        updateData.pickup_total_wait_time_minutes = (delivery.pickup_total_wait_time_minutes || 0) + extensionRequest.additional_minutes;
        updateData.pickup_wait_extension_count = (delivery.pickup_wait_extension_count || 0) + 1;
      }

      const { error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id);

      if (updateError) throw updateError;

      await createNotification(
        extensionRequest.requested_by,
        approve ? 'pickup_extension_approved' : 'pickup_extension_denied',
        approve ? 
          t('package_actions.extension_approved_notification', { minutes: extensionRequest.additional_minutes }) :
          t('package_actions.extension_denied_notification'),
        delivery.id
      );

      await addSystemMessage(
        approve ? 
          t('package_actions.extension_approved_system', { minutes: extensionRequest.additional_minutes }) :
          t('package_actions.extension_denied_system')
      );

      addToast({
        type: approve ? 'success' : 'info',
        title: approve ? t('package_actions.extension_approved') : t('package_actions.extension_denied'),
        description: approve ? 
          t('package_actions.extra_time_added') : 
          t('package_actions.extension_was_denied')
      });

    } catch (error) {
      console.error('Error responding to extension:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_responding_extension')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle no-show report
  const handleNoShowReport = async () => {
    setLoading(true);
    
    try {
      // Get location proof if possible
      let locationProof = null;
      try {
        locationProof = await getCurrentLocation();
      } catch (error) {
        console.log('Could not get location for no-show report:', error);
      }

      const noShowData = {
        reported_by: currentUser.id,
        reported_at: new Date().toISOString(),
        location_proof: locationProof,
        phase: 'pickup'
      };

      const { error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          pickup_no_show_report: noShowData,
          status: isPackageSender ? 'pickup_carrier_no_show' : 'pickup_sender_no_show',
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id);

      if (updateError) throw updateError;

      // Get other user ID for notification
      const otherUserId = isPackageSender ? delivery.carrier_id : delivery.sender_id;

      await createNotification(
        otherUserId,
        'pickup_no_show_reported',
        t('package_actions.no_show_reported_notification', { 
          reporter: userRole 
        }),
        delivery.id
      );

      await addSystemMessage(
        t('package_actions.no_show_reported_system', { 
          reporter: userRole,
          missing: isPackageSender ? t('package_actions.carrier') : t('package_actions.sender')
        })
      );

      addToast({
        type: 'info',
        title: t('package_actions.no_show_reported'),
        description: t('package_actions.pickup_cancelled')
      });

      setShowNoShowDialog(false);

    } catch (error) {
      console.error('Error reporting no-show:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_reporting_no_show')
      });
    } finally {
      setLoading(false);
    }
  };

  // Get pending extension request
  const pendingExtension = (delivery.pickup_extension_requests || [])
    .find(req => req.status === 'pending');

  // Render timer display
  const renderTimer = () => {
    if (!delivery?.pickup_timer_started_at) return null;

    const isExpired = timeRemaining <= 0;
    
    return (
      <div className="PAC-timer-section">
        <div className={`PAC-timer-display ${isExpired ? 'expired' : ''}`} 
             style={{ color: isExpired ? '#EF4444' : 'var(--accent-color2)' }}>
          {formatTimeRemaining(timeRemaining)}
        </div>
        <div className="PAC-timer-status">
          {isExpired ? 
            t('package_actions.waiting_time_expired') : 
            t('package_actions.waiting_time_remaining')
          }
        </div>
      </div>
    );
  };

  // Render presence indicators
  const renderPresenceIndicators = () => (
    <div className="PAC-presence-indicators">
      <div className={`PAC-presence-indicator ${senderHere ? 'present' : ''}`}>
        <div className="PAC-presence-icon">
          {senderHere ? <FaCheckCircle /> : <FaExclamationTriangle />}
        </div>
        <div className="PAC-presence-label">
          {t('package_actions.sender')}
        </div>
      </div>
      
      <div className={`PAC-presence-indicator ${carrierHere ? 'present' : ''}`}>
        <div className="PAC-presence-icon">
          {carrierHere ? <FaCheckCircle /> : <FaExclamationTriangle />}
        </div>
        <div className="PAC-presence-label">
          {t('package_actions.carrier')}
        </div>
      </div>
    </div>
  );

  // Handle different status scenarios
  if (delivery?.status === 'pickup_both_present') {
    return (
      <div className="PAC-meeting-section">
        <div className="PAC-meeting-title">
          <FaUsers size={14} />
          {t('package_actions.ready_for_pickup')}
        </div>
        
        {renderPresenceIndicators()}
        
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <div style={{ fontSize: '11px', marginBottom: '8px', color: 'var(--accent-color)' }}>
            {t('package_actions.both_parties_confirmed')}
          </div>
          
          {isCarrier ? (
            <CustomButton10Co
              onClick={handlePickupComplete}
              label={t('package_actions.confirm_pickup')}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : <FaBox />}
            </CustomButton10Co>
          ) : (
            <div style={{ fontSize: '10px', opacity: 0.8 }}>
              {t('package_actions.waiting_carrier_pickup')}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="PAC-meeting-section">
      <div className="PAC-meeting-title">
        <FaClock size={14} />
        {t('package_actions.pickup_waiting')}
      </div>
      
      {renderTimer()}
      {renderPresenceIndicators()}
      
      {/* Extension request handling */}
      {pendingExtension && pendingExtension.requested_by !== currentUser?.id && (
        <div className="PAC-extension-section">
          <div className="PAC-extension-title">
            {t('package_actions.extension_request')}
          </div>
          <div className="PAC-extension-message">
            {t('package_actions.extension_request_message', { 
              role: isPackageSender ? t('package_actions.carrier') : t('package_actions.sender'),
              minutes: pendingExtension.additional_minutes 
            })}
          </div>
          <div className="PAC-action-buttons-v2">
            <CustomButton10Co
              onClick={() => handleExtensionResponse(true, pendingExtension)}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.approve')}
            </CustomButton10Co>
            <CustomButton10Ca
              onClick={() => handleExtensionResponse(false, pendingExtension)}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.deny')}
            </CustomButton10Ca>
          </div>
        </div>
      )}
      
      {/* Action buttons - show different options based on user presence */}
      {!pendingExtension && timerStarted && (
        <div className="PAC-action-buttons-v2">
          {/* Show "I'm here" button for users who haven't marked as present yet */}
          {!(isPackageSender ? senderHere : carrierHere) && (
            <CustomButton10Co
              onClick={() => setShowArrivalDialog(true)}
              label={t('package_actions.im_here')}
              disabled={loading}
            />
          )}
          
          {/* Show extension button only for users who are NOT marked as present yet */}
          {!(isPackageSender ? senderHere : carrierHere) && (
            <CustomButton10
              onClick={() => setShowExtensionDialog(true)}
              label={t('package_actions.request_extension')}
              disabled={loading}
            />
          )}
          
          {/* Show no-show button only when timer expired and user is present */}
          {timeRemaining <= 0 && (isPackageSender ? senderHere : carrierHere) && (
            <CustomButton10Ca
              onClick={() => setShowNoShowDialog(true)}
              label={t('package_actions.report_no_show')}
              disabled={loading}
            />
          )}
        </div>
      )}
      
      {/* Message for users when timer hasn't started yet */}
      {!pendingExtension && !timerStarted && (
        <div style={{ textAlign: 'center', fontSize: '11px', opacity: 0.8, marginTop: '8px' }}>
          {t('package_actions.waiting_for_timer_start')}
        </div>
      )}
      
      {/* Arrival confirmation dialog */}
      {showArrivalDialog && (
        <ArrivalConfirmDialog
          onCancel={() => setShowArrivalDialog(false)}
          onConfirm={(shareLocation) => handleArrivalConfirmation(shareLocation)}
          isConfirming={loading}
          userRole={userRole}
        />
      )}
      
      {/* Extension request dialog */}
      {showExtensionDialog && (
        <div className="PAC-modal-overlay-v2">
          <div className="PAC-modal2">
            <h3>{t('package_actions.request_extension')}</h3>
            <p>{t('package_actions.extension_dialog_message')}</p>
            <div className="PAC-modal-buttons">
              <CustomButton10Ca
                onClick={() => setShowExtensionDialog(false)}
                label={t('buttons.cancel')}
                disabled={loading}
              />
              <CustomButton10Co
                onClick={() => handleExtensionRequest(3)}
                disabled={loading}
              >
                {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('package_actions.request_extension')}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      )}
      
      {/* No-show report dialog */}
      {showNoShowDialog && (
        <div className="PAC-modal-overlay-v2">
          <div className="PAC-modal2">
            <h3>{t('package_actions.report_no_show')}</h3>
            <p>{t('package_actions.no_show_dialog_message', { 
              role: isPackageSender ? t('package_actions.carrier') : t('package_actions.sender')
            })}</p>
            <div className="PAC-modal-buttons">
              <CustomButton10Ca
                onClick={() => setShowNoShowDialog(false)}
                label={t('buttons.cancel')}
                disabled={loading}
              />
              <CustomButton10Co
                onClick={handleNoShowReport}
                label={t('package_actions.confirm_no_show')}
                disabled={loading}
              >
                {loading && <Spinner size="small" showText={false} thickness={2} />}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Arrival Confirmation Dialog Component
const ArrivalConfirmDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  userRole
}) => {
  const { t } = useTranslation('common');
  const [shareLocation, setShareLocation] = useState(false);

  return ReactDOM.createPortal(
    <div 
      className="PAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isConfirming) {
          onCancel();
        }
      }}
    >
      <div className="PAC-modal2-v2">
        <div className="PAC-location-dialog">
          <div className="PAC-dialog-content">
            <h4>{t('package_actions.confirm_your_arrival')}</h4>
            <p>{t('package_actions.mark_yourself_as_here')}</p>
            
            <div className="PAC-location-option">
              <label className="PAC-checkbox-label">
                <input
                  type="checkbox"
                  checked={shareLocation}
                  onChange={(e) => setShareLocation(e.target.checked)}
                  disabled={isConfirming}
                />
                <span>{t('package_actions.share_location_proof')}</span>
              </label>
              <p className="PAC-location-help">
                {t('package_actions.optional_gps_proof')}
              </p>
            </div>
            
            <div className="PAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isConfirming}
              />
              <CustomButton10Co
                onClick={() => onConfirm(shareLocation)}
                disabled={isConfirming}
              >
                {isConfirming ? (
                  <Spinner size="small" showText={false} />
                ) : (
                  t('package_actions.confirm_arrival')
                )}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PickupWaitingStatus;